import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";

import { Button, AccountTicket } from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";
import ModifyPendingTransactionModal from "pages/transactions/_components/ModifyPendingTransactionModal";
import AccountSummaryData from "pages/accounts/AccountSummaryData";
import AccountOpeningData from "pages/accounts/AccountOpeningData";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { actions as transactionsActions, selectors as transactionsSelectors } from "reducers/transactions";

import { downloadPdf } from "util/download";
import * as i18n from "util/i18n";
import {
    canUserSignPendingFromPotentialSchemes,
    isMvp3PendingTransactionsEnabled,
    isTransactionPendingProcessing,
} from "util/transaction";
import * as accountsMiddleware from "middleware/accounts";
import { getPostLoginHomePath } from "util/v3UxConfig";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";

const V3_ACCOUNTS_CREATE_PATH = "/v3/accounts/create";

function AccountTicketV3Page(props) {
    const {
        match,
        dispatch,
        openingData,
        fullName,
        lastPathname,
        isDesktop,
        loggedUser,
        potentialSignaturesData,
        administrationScheme,
    } = props;
    const [isModalDisplayed, setIsModalDisplayed] = useState(false);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);
    const [signaturesDrawerOpenMobile, setSignaturesDrawerOpenMobile] = useState(false);

    const isPending = openingData?.idTransactionStatus === "PENDING";

    useEffect(() => {
        if (!openingData) {
            dispatch(push("/v3/accounts/create"));
            return;
        }
        dispatch(transactionsActions.getPotentialSignaturesData(openingData.idTransaction));
    }, [dispatch, openingData]);

    const exportPdf = useCallback(async () => {
        if (!openingData) {
            return;
        }
        setExportPdfLoading(true);
        try {
            const { currency, accountType, message, accountNumber, cbu, accountStatus } = openingData;
            const creationDate = openingData.creationDate ?? openingData.creationDateTime;
            const idComprobante = openingData.idTransaction.substring(0, 8).toUpperCase();
            const { data } = await accountsMiddleware.accountOpeningConfirmationPdf(
                currency,
                accountType,
                message,
                accountNumber,
                cbu,
                accountStatus,
                idComprobante,
                creationDate,
            );

            const { content, fileName } = data.data;
            downloadPdf(fileName, content);
            dispatch(
                notificationActions.showNotification(i18n.get("global.successDownload"), "success", [
                    "accountOpeningConfirmation",
                ]),
            );
        } finally {
            setExportPdfLoading(false);
        }
    }, [dispatch, openingData]);

    const rightContentMobile = useMemo(
        () => (
            <Button
                aria-label={isPending ? "Ver esquemas de firmas" : "Descargar comprobante"}
                variant="text"
                color="white"
                icon={isPending ? "Eye" : "SendDown"}
                iconVariant="outline"
                onClick={() => {
                    if (isPending) {
                        setSignaturesDrawerOpenMobile(true);
                    } else {
                        exportPdf();
                    }
                }}
            />
        ),
        [isPending],
    );

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        const headerTitle = i18n.get("accounts.new.account.internal.label");
        dispatchV3ShellLayout(dispatch, {
            headerTitle,
            titleKey: "",
            goBackPath: lastPathname || "/v3/accounts",
            scopes: ["accountOpeningConfirmation"],
            rightContentMobile,
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
        };
    }, [dispatch, lastPathname, rightContentMobile]);

    const getLabels = useCallback((id) => i18n.get(id), []);

    const transactionMoment = useMemo(() => {
        if (!openingData) {
            return undefined;
        }
        return openingData.creationDate ?? openingData.creationDateTime;
    }, [openingData]);

    const preparedBy = useMemo(() => {
        if (!openingData) {
            return { userFullName: fullName, creationDateTime: undefined };
        }
        return {
            userFullName: openingData.creatorFullName || fullName,
            creationDateTime: transactionMoment,
        };
    }, [openingData, fullName, transactionMoment]);

    const signedBy = useMemo(() => {
        if (!openingData?.signatures?.length) {
            return [];
        }
        return openingData.signatures.map((s) => ({
            userFirstName: s.userFirstName,
            userLastName: s.userLastName,
            userFullName: s.userFullName,
            creationDateTime: s.creationDateTime ?? s.modificationDateTime ?? s.submitDateTime ?? s.creationDate,
        }));
    }, [openingData]);

    const releasedBy = useMemo(() => {
        if (!openingData?.liberatorUser || openingData?.idTransactionStatus !== "FINISHED") {
            return undefined;
        }
        return {
            userFullName: openingData.liberatorUser,
            creationDateTime: openingData.liberatorTime,
        };
    }, [openingData]);

    const openingDataInput = useMemo(() => {
        if (!openingData) {
            return {};
        }
        return {
            idTransactionStatus: openingData.idTransactionStatus,
            creationDate: transactionMoment,
            idTransaction: openingData.idTransaction,
            accountType: openingData.accountType,
            currency: openingData.currency,
            message: openingData.message,
        };
    }, [openingData, match?.params?.id, match?.params?.status, transactionMoment]);

    const signTransaction = useCallback(() => {
        if (!openingData) {
            return;
        }
        const signTransactionData = (
            <AccountSummaryData
                openingData={openingData}
                FORM_ID="accounts.new.account"
                signature
                fullName={fullName}
            />
        );

        const ticketPath = `/v3/account/create/${encodeURIComponent(
            String(openingData.idTransactionStatus),
        )}/${encodeURIComponent(String(openingData.idTransaction))}`;
        dispatch(
            push({
                pathname: "/v3/accountOpening/summary",
                state: { goBackState: ticketPath },
            }),
        );
        dispatch(
            formActions.signTransactionData({
                idTransaction: openingData.idTransaction,
                idActivity: openingData.idActivity,
                idForm: "",
                content: signTransactionData,
                title: "new.account",
                pendingDispatch: openingData.pendingDispatch,
            }),
        );

        dispatch(
            formActions.signTransactionPreview({
                idActivity: openingData.idActivity,
                idTransaction: openingData.idTransaction,
                idForm: "",
                ticketData: openingData,
            }),
        );
        dispatch(accountsActions.accountOpeningData({ ...openingData, action: "sign" }));
    }, [dispatch, openingData, fullName]);

    const handleGoBack = useCallback(() => {
        const targetPath = lastPathname || "/v3/accounts";
        if (targetPath === V3_ACCOUNTS_CREATE_PATH) {
            dispatch(accountsActions.clearOpeningData());
        }
        dispatch(push(targetPath));
    }, [dispatch, lastPathname]);

    const goToHistoricTransactions = useCallback(() => {
        dispatch(transactionsActions.setFilters(null));
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const goToPendingAuthorizations = useCallback(() => {
        if (!openingData) {
            return;
        }
        dispatch(push("/pendingTransaction/list"));
        if (!isMvp3PendingTransactionsEnabled()) {
            dispatch(transactionsActions.initPendings());
            dispatch(
                transactionsActions.loadListRequest(
                    {
                        pageNumber: 1,
                        status: "PENDING",
                        filterPendingDispatch: false,
                        functionRequest: "pendingTransactions",
                        idActivity: openingData.idActivity,
                    },
                    true,
                    false,
                ),
            );
        }
    }, [dispatch, openingData]);

    const cancelPendingTransaction = useCallback(() => {
        if (!openingData) {
            return;
        }
        const ticketPath = `/v3/account/create/${encodeURIComponent(
            String(openingData.idTransactionStatus),
        )}/${encodeURIComponent(String(openingData.idTransaction))}`;
        const cancelContent = (
            <>
                <AccountSummaryData
                    openingData={openingData}
                    signature
                    FORM_ID="accounts.new.account"
                    fullName={openingData.fullName}
                />
                <AccountOpeningData />
            </>
        );
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: openingData.idTransaction,
                content: cancelContent,
                title: "accounts.new.account.internal.label",
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity: openingData.idActivity, idForm: "" }));
        dispatch(accountsActions.accountOpeningData({ ...openingData, action: "cancel" }));
        dispatch(
            push({
                pathname: "/v3/accountOpening/summary",
                state: { goBackState: ticketPath },
            }),
        );
    }, [dispatch, openingData]);

    const isPendingProcessing = useMemo(() => (openingData ? isTransactionPendingProcessing(openingData) : false), [
        openingData,
    ]);

    const showSignButton = useMemo(() => {
        if (!openingData || !isPending || isPendingProcessing || openingData.control) {
            return false;
        }
        return canUserSignPendingFromPotentialSchemes(openingData, loggedUser, potentialSignaturesData);
    }, [openingData, isPending, isPendingProcessing, loggedUser, potentialSignaturesData]);

    const navigationButtons = useMemo(() => {
        if (!openingData) {
            return [];
        }
        if (openingData.idTransactionStatus === "FINISHED") {
            return [
                {
                    onClick: exportPdf,
                    icon: "SendDown",
                    label: getLabels("global.download"),
                    loading: exportPdfLoading,
                    css: { paddingLeft: 40, paddingRight: 40 },
                },
            ];
        }

        return [];
    }, [openingData, exportPdf, exportPdfLoading, getLabels]);

    const footerNavigationButtons = useMemo(() => {
        if (!openingData) {
            return [];
        }
        const homePath = getPostLoginHomePath(isUxDashboardImprovementsEnabled());

        if (isPending) {
            const buttons = [
                {
                    onClick: goToPendingAuthorizations,
                    icon: "WritingPending",
                    label: getLabels("goto.pendings.transaction.autorizaciones"),
                    variant: "outline",
                },
            ];
            if (!isPendingProcessing && !openingData.control) {
                buttons.push({
                    onClick: cancelPendingTransaction,
                    icon: "Trash",
                    label: getLabels("transaction.delete"),
                    variant: "outline",
                });
            }
            if (!isPendingProcessing) {
                buttons.push({
                    onClick: () => setIsModalDisplayed(true),
                    icon: "Pencil",
                    label: getLabels("global.modify"),
                    variant: "outline",
                });
            }
            if (showSignButton) {
                buttons.push({
                    onClick: signTransaction,
                    icon: "AddServices",
                    label: getLabels("forms.signTransaction.link"),
                    variant: "solid",
                });
            }
            return buttons;
        }

        return [
            {
                onClick: goToHistoricTransactions,
                icon: "Refund",
                label: getLabels("goto.historic.transaction"),
                variant: "outline",
            },
            {
                onClick: () => dispatch(push(homePath)),
                icon: "Home",
                label: getLabels("new.check.right.buttom"),
                variant: "solid",
            },
        ];
    }, [
        openingData,
        isPending,
        isPendingProcessing,
        dispatch,
        getLabels,
        goToHistoricTransactions,
        goToPendingAuthorizations,
        cancelPendingTransaction,
        showSignButton,
        signTransaction,
    ]);

    if (!openingData) {
        return null;
    }

    return (
        <>
            <AccountTicket
                goBack={handleGoBack}
                getLabels={getLabels}
                openingData={openingDataInput}
                formatOperationDate={formatLegacyWebDateTimeString}
                transactionTicketProps={{ formatSignatureDate: formatLegacyWebDateTimeString }}
                preparedBy={preparedBy}
                signedBy={signedBy}
                releasedBy={releasedBy}
                navigationButtons={navigationButtons}
                footerNavigationButtons={footerNavigationButtons}
                potentialSignaturesData={potentialSignaturesData ?? undefined}
                signatureSchemeDisplayMode={administrationScheme === "medium" ? "medium" : "advanced"}
                openSignatureSchemeMobile={signaturesDrawerOpenMobile}>
                <ModifyPendingTransactionModal
                    isDesktop={isDesktop}
                    isDisplayed={isModalDisplayed}
                    handleDismiss={() => setIsModalDisplayed(false)}
                    dispatch={dispatch}
                    idTransaction={openingData.idTransaction}
                    cancelTransactionData={() => (
                        <AccountSummaryData
                            openingData={openingData}
                            signature
                            FORM_ID="accounts.new.account"
                            fullName={openingData.fullName}
                        />
                    )}
                />
            </AccountTicket>
        </>
    );
}

AccountTicketV3Page.propTypes = {
    match: shape({
        params: shape({
            status: string,
            id: string,
        }),
    }).isRequired,
    dispatch: func.isRequired,
    openingData: shape({}),
    fullName: string.isRequired,
    lastPathname: string.isRequired,
    isDesktop: bool.isRequired,
    loggedUser: shape({}),
    potentialSignaturesData: arrayOf(shape({})),
    administrationScheme: string,
};

AccountTicketV3Page.defaultProps = {
    openingData: null,
    loggedUser: null,
    potentialSignaturesData: null,
    administrationScheme: "advanced",
};

const mapStateToProps = (state) => ({
    openingData: accountsSelectors.getAccountOpeningData(state),
    fullName: sessionSelectors.getUser(state).userFullName,
    lastPathname: statusSelectors.getPathname(state),
    loggedUser: sessionSelectors.getUser(state),
    potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
    administrationScheme: sessionSelectors.getAdministrationScheme(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountTicketV3Page);
